import dayjs from 'dayjs';

import { toTime, toUtcDate } from '@src/utils';

import { ABSENCE_BLOCK_POSITION_SINGLE } from '../constants';

export function getSlotDatefromSelection(info) {
  let slot = {
    resource_id: info.resource.id,
    user_id: info.resource.extendedProps.user_id,
    absences_ids: [],
  };
  if (info.allDay) {
    slot = {
      ...slot,
      date_from: info.startStr,
      date_to: toUtcDate(dayjs(info.endStr).subtract(1, 'day')),
      end: toUtcDate(dayjs(info.end).subtract(1, 'day')),
    };
  } else {
    slot = {
      ...slot,
      date_from: toUtcDate(info.start),
      date_to: toUtcDate(info.end),
      startTime: toTime(info.start),
      endTime: toTime(info.end),
    };
  }
  return slot;
}

/**
     Returns an array of free dates (YYYY-MM-DD) within the slot range that are
     not occupied by absences.
     - slot: { date_from: 'YYYY-MM-DD', date_to: 'YYYY-MM-DD', ... }
     - absences: Array<{ date: 'YYYY-MM-DD', ... }>
*/
export function getSlotFreeDates(slot, absences) {
  if (!slot || !slot.date_from || !slot.date_to) {
    return [];
  }

  const start = dayjs(slot.date_from, 'YYYY-MM-DD', true);
  const end = dayjs(slot.date_to, 'YYYY-MM-DD', true);

  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return [];
  }

  const occupied = new Set(
    (absences || [])
      .map((a) => a?.acti_date)
      .filter(Boolean)
      .map((d) => dayjs(d, 'YYYY-MM-DD', true))
      .filter((d) => d.isValid() && !d.isBefore(start) && !d.isAfter(end))
      .map((d) => d.format('YYYY-MM-DD')),
  );

  const free = [];
  let cur = start;

  while (!cur.isAfter(end)) {
    const key = cur.format('YYYY-MM-DD');
    if (!occupied.has(key)) {
      free.push(cur);
    }
    cur = cur.add(1, 'day');
  }

  return free;
}
function isNextDay(a, b) {
  const aNext = a._date.add(1, 'day');
  return aNext.isSame(b._date, 'day');
}

// TODO: check `getAbsenceDataById` it has very similar implementation
//       consider to merge or remove same parts (DRY)
export function getSlotDataFromEvent(event, absences, opts = {}) {
  const { single = false, sameType = () => true } = opts;

  if (!event || !Array.isArray(absences) || absences.length === 0) {
    return null;
  }

  const eventIdStr = String(event.id);
  const anchor = absences.find((a) => String(a.id) === eventIdStr);
  if (!anchor) {
    return null;
  }

  const resource = event.getResources()[0];
  const user_id = resource.extendedProps.user_id;
  if (single || anchor.block_position === ABSENCE_BLOCK_POSITION_SINGLE) {
    return {
      resource_id: resource.id,
      user_id: user_id,
      date_from: event.start,
      date_to: event.start,
      ...(!event.allDay && { startTime: toTime(event.startStr) }),
      ...(!event.allDay && { endTime: toTime(event.endStr) }),
      absences_ids: [parseInt(event.id)],
    };
  }

  // Normalize with parsed date
  const normalized = absences.map((a) => {
    const _date = dayjs(a.acti_date);
    return { ...a, _date };
  });

  const sameGroup = (x) => {
    return (
      String(x.acti_user) === String(anchor.acti_user) && sameType(anchor, x)
    );
  };

  // Collect all candidate items for this user/type
  const candidates = normalized
    .filter(sameGroup)
    .sort((a, b) => a._date.get('date') - b._date.get('date'));

  const anchorIdx = candidates.findIndex((a) => String(a.id) === eventIdStr);
  if (anchorIdx === -1) {
    return null;
  }

  // Expand left while consecutive by date (-1 day steps)
  let left = anchorIdx;
  while (left - 1 >= 0 && isNextDay(candidates[left - 1], candidates[left])) {
    left -= 1;
  }

  // Expand right while consecutive by date (+1 day steps)
  let right = anchorIdx;
  while (
    right + 1 < candidates.length &&
    isNextDay(candidates[right], candidates[right + 1])
  ) {
    right += 1;
  }

  const block = candidates.slice(left, right + 1);
  return {
    resource_id: resource.id,
    user_id: block[0].acti_user,
    date_from: block[0].acti_date,
    date_to: block[block.length - 1].acti_date,
    absences_ids: block.map((a) => a.id),
  };
}
